import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, filter, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ErrorHandlingService } from '../../core/services/error-handling.service';
import { LookupResponse } from '../../core/models/lookup.models';
import { TAB_NAME } from '../../core/constants/tab.constant';
import {
  NzNotificationPlacement,
  NzNotificationService,
} from 'ng-zorro-antd/notification';
import {
  ApiResponse,
  ApiResponses,
  BehaviourLookUpModel,
  BehaviourSubListModel,
} from '../../core/models/common.models';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import {
  BIP_TEMPLATE,
  DASHBOARD_ROUTES,
  HOME_ROUTES,
  INSIGHTS_ROUTE,
  NOTIFICATION,
  ORGANIZATIONS,
  STRATEGIES,
  USER_MANAGEMENT,
  USER_SEARCH,
} from '../../core/constants/routes.constant';
import { addDays, format, getDay, startOfWeek } from 'date-fns';
import { ErrorResponse } from '../../core/models/error.models';
import {
  OrganizationModel,
  userDetailModel,
} from '../../features/organizations/organization.interface';
import { OrganizationService } from '../../features/organizations/organization.service';
import { OrganizationOption } from '../../components/organization-search/organization-search.component';
import { OrganizationSelectionService } from '../../core/services/organization-selection.service';
enum NotifyClassEnum {
  success = 'notify-success',
  error = 'notify-error',
  warning = 'notify-warning',
  info = 'notify-info',
  blank = 'notify-blank',
}

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private readonly API_URL = `${environment.apiUrl}`;
  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlingService);
  private readonly notificationService = inject(NzNotificationService);
  private readonly orgService = inject(OrganizationService);
  private readonly orgSelectionService = inject(OrganizationSelectionService);

  private readonly activeTabSubject = new BehaviorSubject<string>(
    TAB_NAME.PRE_BIP
  );
  activeTab$ = this.activeTabSubject.asObservable();
  private readonly orgListSubject = new BehaviorSubject<OrganizationOption[]>(
    []
  );
  orgList$ = this.orgListSubject.asObservable();

  constructor(private readonly router: Router) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: RouterEvent) => {
        this.currentRouteSubject.next(event.url); // Update the route
      });
  }

  getDataByType(
    typeName: string,
    searchTerm?: string
  ): Observable<LookupResponse> {
    const api$ = searchTerm
      ? this.http.get<LookupResponse>(
          `${this.API_URL}/ApplicationLookup?typeName=${typeName}&searchTerm=${searchTerm}`
        )
      : this.http.get<LookupResponse>(
          `${this.API_URL}/ApplicationLookup?typeName=${typeName}`
        );

    return api$.pipe(
      map(response => response),
      catchError(error => this.errorHandler.handleError(error))
    );
  }

  getBehaviourLookups() {
    return this.http
      .get<
        ApiResponse<BehaviourLookUpModel>
      >(`${this.API_URL}/Behavior/lookups`)
      .pipe(
        map(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getBehaviourSubTypes(
    behaviourId: number
  ): Observable<ApiResponses<BehaviourSubListModel>> {
    return this.http
      .get<
        ApiResponses<BehaviourSubListModel>
      >(`${this.API_URL}/Behavior/get-types-by-behavior-category-id?BehaviorCategoryId=${behaviourId}`)
      .pipe(
        map(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  notify(
    type: 'success' | 'info' | 'warning' | 'error' | 'blank',
    messages: string | string[],
    messageTitle?: string,
    width = '400px',
    placement: NzNotificationPlacement = 'top',
    duration = 5000
  ) {
    const baseConfig = {
      nzPlacement: placement,
      nzDuration: duration,
      nzClass: NotifyClassEnum[type],
    };

    if (Array.isArray(messages) && messages.length > 1) {
      const content = messages.map(msg => `• ${msg}`).join('<br>');

      this.notificationService.create(
        type,
        messageTitle ?? 'Multiple Issues Found',
        content,
        {
          ...baseConfig,
          nzStyle: {
            width: '600px',
            color: '#fff',
          },
        }
      );
      return;
    }

    const message = Array.isArray(messages) ? messages[0] : messages;

    this.notificationService.create(type, message, '', {
      ...baseConfig,
      nzStyle: {
        width: width,
      },
    });
  }

  clearAllNotifications(): void {
    this.notificationService.remove();
  }

  // Method to set the active tab
  setActiveTab(tabName: string): void {
    this.activeTabSubject.next(tabName);
  }

  // Method to get the current active tab
  getActiveTab(): string {
    return this.activeTabSubject.value;
  }

  private readonly currentRouteSubject = new BehaviorSubject<string>('');

  // Observable to expose the current route
  public currentRoute$ = this.currentRouteSubject.asObservable();

  public isInsightAdminRoute$ = this.currentRoute$.pipe(
    map(currentRoute =>
      INSIGHTS_ROUTE.some(route => currentRoute.includes(route))
    )
  );
  public isOrgRoute$ = this.currentRoute$.pipe(
    map(currentRoute =>
      ORGANIZATIONS.some(route => currentRoute.includes(route))
    )
  );
  public isDashboardRoute$ = this.currentRoute$.pipe(
    map(currentRoute =>
      DASHBOARD_ROUTES.some(route => currentRoute.includes(route))
    )
  );
  public isHomeRoute$ = this.currentRoute$.pipe(
    map(currentRoute => {
      const homeRoutes: readonly string[] = HOME_ROUTES;
      const normalizedRoute = currentRoute.split('?')[0].split('#')[0];
      const sanitizedRoute =
        normalizedRoute.length > 1 && normalizedRoute.endsWith('/')
          ? normalizedRoute.slice(0, -1)
          : normalizedRoute;

      return homeRoutes.includes(sanitizedRoute);
    })
  );
  public isUserManageRoute$ = this.currentRoute$.pipe(
    map(currentRoute =>
      USER_MANAGEMENT.some(route => currentRoute.includes(route))
    )
  );
  public isBipTemplateRoute$ = this.currentRoute$.pipe(
    map(currentRoute =>
      BIP_TEMPLATE.some(route => currentRoute.includes(route))
    )
  );

  public isNewStrategyRoute$ = this.currentRoute$.pipe(
    map(currentRoute => STRATEGIES.some(route => currentRoute.includes(route)))
  );

  isUserSearchRoute$ = this.currentRoute$.pipe(
    map(currentRoute => USER_SEARCH.some(route => currentRoute.includes(route)))
  );

  public isNotificationRoute$ = this.currentRoute$.pipe(
    map(currentRoute =>
      NOTIFICATION.some(route => currentRoute.includes(route))
    )
  );

  public isBroadcastRoute$ = this.currentRoute$.pipe(
    map(currentRoute => currentRoute.includes('broadcast-config'))
  );

  convertImageToBase64(url: string): Promise<string> {
    return this.http
      .get(url, { responseType: 'blob' })
      .toPromise()
      .then(blob => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob as Blob);
        });
      });
  }

  async getBase64ImageFromUrl(imageUrl: string): Promise<string> {
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        if (!result.startsWith('data:image')) {
          console.warn(
            'Warning: Base64 string does not contain image metadata'
          );
        }
        resolve(result); // Returns the full Base64 string
      };
      reader.onerror = () =>
        reject(new Error('Failed to read image as Base64'));
      reader.readAsDataURL(blob);
    });
  }

  getWeekDates(baseDate: Date): { date: string; day: string }[] {
    const weekDates: { date: string; day: string }[] = [];
    const dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];

    // Get Sunday as the start of the week
    const sunday = startOfWeek(baseDate, { weekStartsOn: 0 });

    // Generate all dates for the week
    for (let i = 0; i < 7; i++) {
      const date = addDays(sunday, i);
      weekDates.push({
        date: format(date, 'yyyy-MM-dd'), // Format as YYYY-MM-DD
        day: dayNames[getDay(date)], // Get the correct day name
      });
    }

    return weekDates;
  }

  getRangeDates(
    startDate: Date,
    endDate: Date
  ): { date: string; day: string }[] {
    const weekDates: { date: string; day: string }[] = [];
    const dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];

    const current = new Date(startDate);
    while (current <= endDate) {
      weekDates.push({
        date: format(current, 'yyyy-MM-dd'),
        day: dayNames[getDay(current)],
      });
      current.setDate(current.getDate() + 1);
    }

    return weekDates;
  }

  getOrgList(userData: userDetailModel) {
    if (userData?.isSuperAdmin) {
      this.getDataByType('Organization', '').subscribe({
        next: response => {
          this.orgListSubject.next(response.result);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.notify('error', errorMessages || message);
        },
      });
    } else {
      this.orgService.getOrganizationsToSwitch().subscribe({
        next: response => {
          if (response?.result) {
            const orgList = response.result.map((item: OrganizationModel) => ({
              id: item.id ?? 0,
              value: item.organizationName ?? '',
              parentOrganizationId: item.parentOrganizationId ?? null,
            }));
            const selectedOrg = orgList.find(
              org => org.parentOrganizationId === null
            );
            this.orgSelectionService.setParentOrgId(
              selectedOrg ? selectedOrg.id : null
            );

            this.orgListSubject.next(orgList);
          } else {
            this.orgListSubject.next([]);
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.notify('error', errorMessages || message);
        },
      });
    }
  }
}
